import { useState, useCallback } from 'react'
import styled from 'styled-components'
import SvgAvatar from '@capture/capture-components/src/icons/Avatar'
import SvgCheck from '@capture/capture-components/src/icons/Check'
import { _ } from '~/assets/localization/util'
import { colors, mediaQueries } from '~/assets/styleConstants'
import { isEmail } from '~/utilities/inputValidation'
import { Button } from '../Common/Button'
import { FilledTextButton } from '../Common/IconTextButton'
import { TextInputFormRow } from '../Common/TextInputFormRow'

const Wrapper = styled.div`
    position: relative;
    width: 400px;

    ${mediaQueries.mobile} {
        width: 100%;
    }
`

const isRecipientValid = (value: string): boolean => {
    return value.split(',').every(isEmail)
}

const isSubjectValid = (value: string): boolean => {
    return value !== ''
}

export type EmailSharingOptions = {
    recipients: string
    subject: string
    message: string
}

type FormField = {
    value: string
    isValid?: boolean
}

type ShareToEmailFormProps = {
    onConfirm: (options: EmailSharingOptions) => void
}

export function ShareToEmailForm({ onConfirm }: ShareToEmailFormProps) {
    const [recipients, setRecipients] = useState<FormField>({
        value: '',
    })
    const [subject, setSubject] = useState<FormField>({ value: '' })
    const [message, setMessage] = useState<FormField>({
        value: '',
        isValid: true,
    })

    const validateAllFormFields = useCallback((): boolean => {
        const isValidRecipients = isRecipientValid(recipients.value)
        const isValidSubject = isSubjectValid(subject.value)

        setRecipients((prev) => ({ ...prev, isValid: isValidRecipients }))
        setSubject((prev) => ({ ...prev, isValid: isValidSubject }))

        return isValidRecipients && isValidSubject
    }, [recipients.value, subject.value])

    const isAllFieldsValid = useCallback((): boolean => {
        return (
            recipients.isValid === true &&
            subject.isValid === true &&
            message.isValid === true
        )
    }, [recipients.isValid, subject.isValid, message.isValid])

    const handleRecipientsChanged = (newValue: string) => {
        setRecipients({
            value: newValue,
            isValid: isRecipientValid(newValue),
        })
    }

    const handleSubjectChanged = (newValue: string) => {
        setSubject({
            value: newValue,
            isValid: isSubjectValid(newValue),
        })
    }

    const handleMessageChanged = (newValue: string) => {
        setMessage({
            value: newValue,
            isValid: true,
        })
    }

    const handleEmailOptionsConfirmed = useCallback(() => {
        if (validateAllFormFields()) {
            onConfirm({
                recipients: recipients.value,
                subject: subject.value,
                message: message.value,
            })
        }
    }, [
        recipients.value,
        subject.value,
        message.value,
        validateAllFormFields,
        onConfirm,
    ])

    const isFormComplete = isAllFieldsValid()
    const confirmButton = Button(_('done'), handleEmailOptionsConfirmed, {
        icon: SvgCheck,
        isDisabled: !isFormComplete,
    })

    return (
        <Wrapper>
            <TextInputFormRow
                title={_('send_email_to')}
                isInvalid={recipients.isValid === false}
                value={recipients.value}
                placeholder={_('enter_receivers_address')}
                icon={
                    <SvgAvatar
                        width={16}
                        height={16}
                        color={colors.captureGrey800}
                    />
                }
                onChange={handleRecipientsChanged}
            />
            <TextInputFormRow
                title={_('email_subject')}
                isInvalid={subject.isValid === false}
                value={subject.value}
                placeholder={_('enter_subject')}
                onChange={handleSubjectChanged}
            />
            <TextInputFormRow
                title={_('email_message')}
                value={message.value}
                placeholder={_('enter_message')}
                multiLine={true}
                onChange={handleMessageChanged}
            />
            <FilledTextButton {...confirmButton} />
        </Wrapper>
    )
}
